import type { Partner } from "@expert/shared-types";

import { sendEventMetric } from "@expert/cloudwatch-metrics";

const UI_ORCHESTRATION_COMPONENT = "ui-orchestration";

/** Empty, whitespace-only, or null means the field was not shown. */
export interface DisplayedClaim {
    imei?: string | null;
    claimDate?: string | null;
    status?: string | null;
    replacementDevice?: string | null;
}

// Fixed order so the same fields always produce the same `outcome` value.
const FIELD_ORDER = ["imei", "claimDate", "status", "replacementDevice"] as const;

const getShownFields = (claim: DisplayedClaim) => FIELD_ORDER.filter((field) => !!claim[field]?.trim());

/** Whether any claim field would be shown; the Customer tab uses the same rule to decide whether to render the section. */
export const hasShownClaimFields = (claim: DisplayedClaim): boolean => getShownFields(claim).length > 0;

// Keyed on the claim, not the MDN: InvalidMdnView can change the MDN while the same claim stays in the
// store, and a new lookup can bring a different claim. Module-level so a Customer tab remount doesn't re-count.
const reportedClaims = new Set<string>();

// Fields go in `outcome` (e.g. "claimDate+status") because the metrics collector drops dimension keys
// outside its allowlist.
export function sendReplacementClaimDisplayedMetric(sessionId: string, partner: Partner, claim: DisplayedClaim) {
    const shownFields = getShownFields(claim);
    if (shownFields.length === 0) return;

    const key = `${sessionId}|${claim.imei ?? ""}|${claim.claimDate ?? ""}`;
    if (reportedClaims.has(key)) return;
    reportedClaims.add(key);

    void sendEventMetric("ReplacementClaimDisplayed", {
        partner,
        component: UI_ORCHESTRATION_COMPONENT,
        outcome: shownFields.join("+"),
    });
}
